import { Award, Users, Clock, Heart } from "lucide-react";

export const AboutSection = () => {
  const values = [
    {
      icon: Heart,
      title: "Made with Love",
      description: "Every dish reflects our passion for bringing joy through food"
    },
    {
      icon: Award,
      title: "Quality First",
      description: "Fresh ingredients, authentic recipes, consistent excellence"
    },
    {
      icon: Users,
      title: "People-Centric",
      description: "We don't just cater events — we take care of people"
    },
    {
      icon: Clock,
      title: "Always Reliable",
      description: "Punctual, professional, and prepared for every occasion"
    }
  ];

  return (
    <section
      id="about"
      className="py-20 md:py-32 px-6 md:px-12 lg:px-24 bg-white"
      data-testid="about-section"
    >
      <div className="max-w-7xl mx-auto">
        <div className="grid lg:grid-cols-2 gap-16 items-center">
          {/* Left Content */}
          <div className="space-y-8">
            <div className="space-y-4">
              <p className="text-sm tracking-widest uppercase font-semibold text-orange-700">
                Our Story
              </p>
              <h2 className="font-heading text-4xl md:text-5xl font-semibold tracking-tight text-gray-800">
                Where Home-Style Flavor Meets Event-Ready Execution
              </h2>
            </div>

            <p className="text-lg md:text-xl leading-relaxed text-gray-600">
              Kamaliicious was built on the belief that food should feel personal — even when served at scale. Rooted in home-style cooking and guided by professional standards, we bring intention, care, and consistency to every dish we prepare.
            </p>

            <p className="text-lg leading-relaxed text-gray-600">
              What started as a passion for feeding friends and family has grown into a full-service catering company that serves corporate offices, intimate celebrations, and large-scale events across the region.
            </p>

            {/* Quote */}
            <div className="pt-4 border-l-4 border-orange-700 pl-6">
              <p className="font-accent text-xl italic text-gray-700">
                "We don't just cater events — we take care of people."
              </p>
            </div>

            {/* Values Grid */}
            <div className="grid grid-cols-2 gap-4 pt-8">
              {values.map((value, index) => (
                <div
                  key={index}
                  className="p-4 rounded-xl hover:bg-stone-50 transition-colors duration-300"
                >
                  <div className="w-10 h-10 rounded-full bg-orange-100 flex items-center justify-center mb-3">
                    <value.icon className="w-5 h-5 text-orange-700" />
                  </div>
                  <h3 className="font-semibold text-gray-800">{value.title}</h3>
                  <p className="text-sm text-gray-600 mt-1">{value.description}</p>
                </div>
              ))}
            </div>
          </div>

          {/* Right Image */}
          <div className="relative">
            <div className="relative aspect-[3/4] rounded-2xl overflow-hidden shadow-xl">
              <img
                src="https://images.unsplash.com/photo-1751651054936-db23f5d67160?crop=entropy&cs=srgb&fm=jpg&q=85"
                alt="Chef preparing fresh ingredients"
                className="w-full h-full object-cover"
              />
              {/* Overlay */}
              <div className="absolute inset-0 bg-gradient-to-t from-black/30 to-transparent" />
            </div>

            {/* Stats Card */}
            <div className="absolute -bottom-8 -left-8 bg-white p-6 rounded-xl shadow-xl border border-stone-100 grid grid-cols-2 gap-6">
              <div className="text-center">
                <p className="text-3xl font-heading font-bold text-orange-700">10+</p>
                <p className="text-sm text-gray-600">Years Experience</p>
              </div>
              <div className="text-center">
                <p className="text-3xl font-heading font-bold text-orange-700">5000+</p>
                <p className="text-sm text-gray-600">Happy Guests</p>
              </div>
            </div>

            {/* Decorative elements */}
            <div className="absolute -top-6 -right-6 w-32 h-32 bg-orange-100 rounded-full -z-10" />
            <div className="absolute -bottom-12 right-12 w-20 h-20 bg-green-100 rounded-2xl -z-10" />
          </div>
        </div>
      </div>
    </section>
  );
};

export default AboutSection;
